import './party.css';

import { closeButton } from './hud.js';
import { uiText } from './text.js';

/** A question from another player: its sender, its wording, and how long it stands. */
export interface InviteQuestion {
  from: string;
  text: string;
  expiresInMs: number;
}

/**
 * The question put to an invited player — join a party, become friends:
 * yes or no. It closes by the cross or Escape (a refusal), and on its own
 * when the question lapses on the server, so it never offers what can no
 * longer be taken.
 */
export class InvitePrompt {
  private root?: HTMLElement;
  private timer?: number;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') this.answer(false);
  };
  private current?: InviteQuestion;

  constructor(private readonly onAnswer: (from: string, accept: boolean) => void) {}

  show(invite: InviteQuestion): void {
    this.close();
    this.current = invite;
    const root = document.createElement('div');
    root.className = 'party-invite';
    root.setAttribute('role', 'dialog');
    const text = document.createElement('p');
    text.textContent = uiText(invite.text);
    const actions = document.createElement('div');
    actions.className = 'party-invite__actions';
    const accept = document.createElement('button');
    accept.type = 'button';
    accept.className = 'party-invite__button';
    accept.textContent = uiText('Accepter');
    accept.addEventListener('click', () => {
      this.answer(true);
    });
    const decline = document.createElement('button');
    decline.type = 'button';
    decline.className = 'party-invite__button';
    decline.textContent = uiText('Refuser');
    decline.addEventListener('click', () => {
      this.answer(false);
    });
    actions.append(accept, decline);
    root.append(
      closeButton('Refuser', () => {
        this.answer(false);
      }),
      text,
      actions,
    );
    document.body.append(root);
    this.root = root;
    window.addEventListener('keydown', this.onKey);
    this.timer = window.setTimeout(() => {
      this.close();
    }, invite.expiresInMs);
  }

  private answer(accept: boolean): void {
    const invite = this.current;
    this.close();
    if (invite !== undefined) this.onAnswer(invite.from, accept);
  }

  close(): void {
    window.clearTimeout(this.timer);
    window.removeEventListener('keydown', this.onKey);
    this.root?.remove();
    this.root = undefined;
    this.current = undefined;
  }
}
